import { useSyncExternalStore } from "react";

type Listener = () => void;

/**
 * A minimal external store for feature state that lives outside the SDK's TankStore
 * (notifications, agent runs). Selectors must return stable references for unchanged input.
 */
export class MiniStore<S> {
  private state: S;
  private listeners = new Set<Listener>();

  constructor(initial: S) {
    this.state = initial;
  }

  getState = (): S => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  setState(update: (s: S) => S): void {
    const next = update(this.state);
    if (next === this.state) return;
    this.state = next;
    for (const l of this.listeners) l();
  }
}

export function useMiniStore<S, T>(store: MiniStore<S>, select: (s: S) => T): T {
  return useSyncExternalStore(store.subscribe, () => select(store.getState()));
}

/** One store instance per `TankClient` (the client is the unit of identity for a signed-in user in this tab). */
export function perClient<S>(make: () => S): (client: object) => S {
  const stores = new WeakMap<object, S>();
  return (client) => {
    let s = stores.get(client);
    if (!s) {
      s = make();
      stores.set(client, s);
    }
    return s;
  };
}
